<template>
  <v-card v-if="mounted" width="100%" variant="tonal" data-testid="other-info">
    <v-row no-gutters>
      <v-card-title class="w-100 d-flex justify-space-between">
        {{ t('collaboratorProfilesPage.other') }}
        <c-copy-button data-testid="collaborator-profile-copy-other-info" :get-data="getCopyData" />
      </v-card-title>
      <v-divider />

      <v-card-text>
        <v-row class="pb-3">
          <v-col lg="6" cols="12">
            <v-list-subheader class="text-body-1 pa-0 mb-2 subhead">{{
              t('collaboratorProfilesPage.strengthsWeaknesses')
            }}</v-list-subheader>
            <c-text-editor :editor="swEditor" typing class="ml-1 mr-1" />
          </v-col>

          <v-col lg="6" cols="12">
            <v-list-subheader class="text-body-1 pa-0 mb-2 subhead">{{
              t('collaboratorProfilesPage.otherTechnologies')
            }}</v-list-subheader>
            <c-text-editor :editor="otEditor" typing class="ml-1 mr-1" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-row>
  </v-card>
</template>

<script lang="ts" setup>
import { onMounted, ref, toRefs, watch } from 'vue';
import CTextEditor from '@/components/Common/CTextEditor.vue';
import { Editor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import { erpClient } from '@tfpl/api-client';
import { useCopyData } from '@/composables/useCopyData';
import CCopyButton from '@/components/Common/CCopyButton.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { getCollaboratorProfileOtherCopyData } = useCopyData();

const swEditor = ref();
const otEditor = ref();

const mounted = ref(false);

const props = defineProps<{
  collaboratorProfile: erpClient.CollaboratorProfileOutputDto;
}>();

const emit = defineEmits(['updateProfile']);

const { collaboratorProfile } = toRefs(props);

const emitCollaboratorChange = () => {
  const otherTechnologies = otEditor.value.getHTML() === '<p></p>' ? '' : otEditor.value.getHTML();
  const strengthsWeaknesses = swEditor.value.getHTML() === '<p></p>' ? '' : swEditor.value.getHTML();

  if (collaboratorProfile.value) {
    collaboratorProfile.value.otherTechnologies = otherTechnologies;
    collaboratorProfile.value.strengthsWeaknesses = strengthsWeaknesses;
  } else {
    collaboratorProfile.value = {
      otherTechnologies,
      strengthsWeaknesses,
    } as erpClient.CollaboratorProfileOutputDto;
  }

  emit('updateProfile', collaboratorProfile.value);
};

const makeEditorInstances = () => {
  swEditor.value = new Editor({
    editable: true,
    content: collaboratorProfile.value.strengthsWeaknesses || '',
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),
      TaskList,
      TaskItem,
      Link,
    ],
    onUpdate: () => {
      emitCollaboratorChange();
    },
  });

  otEditor.value = new Editor({
    editable: true,
    content: collaboratorProfile.value.otherTechnologies || '',
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Placeholder.configure({
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),
      TaskList,
      TaskItem,
      Link,
    ],
    onUpdate: () => {
      emitCollaboratorChange();
    },
  });
};

const getCopyData = () => getCollaboratorProfileOtherCopyData(collaboratorProfile.value);

onMounted(() => {
  mounted.value = true;
  makeEditorInstances();
});

watch(
  () => props.collaboratorProfile,
  (newProfile, oldProfile) => {
    if (newProfile.uuid !== oldProfile.uuid) {
      swEditor.value.commands.setContent(newProfile.strengthsWeaknesses || '');
      otEditor.value.commands.setContent(newProfile.otherTechnologies || '');
    }
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.subhead {
  margin-left: 4px;
}
</style>
